import { type ReactNode, useMemo } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { SnackbarProvider } from 'notistack';
import { HelmetProvider } from 'react-helmet-async';

import { useUIStore } from '@/stores/uiStore';
import { createAppTheme } from '@/styles/theme';

/**
 * QueryClient instance with default options.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000, // 30 seconds — shorter for mock mode
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Root providers component.
 * Composes all necessary providers for the application:
 * - QueryClientProvider (React Query)
 * - StyledEngineProvider + ThemeProvider (MUI, dynamic theme)
 * - CssBaseline (MUI)
 * - SnackbarProvider (notistack)
 *
 * The MUI theme is dynamically created based on the selected template
 * in the UI store, so switching templates instantly updates the entire app.
 */
export function Providers({ children }: { children: ReactNode }): JSX.Element {
  const themeTemplate = useUIStore((s) => s.themeTemplate);

  const theme = useMemo(
    () => createAppTheme(themeTemplate),
    [themeTemplate],
  );

  return (
    <QueryClientProvider client={queryClient}>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <HelmetProvider>
            <SnackbarProvider
              maxSnack={3}
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
              autoHideDuration={3000}
              preventDuplicate
            >
              {children}
            </SnackbarProvider>
          </HelmetProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    </QueryClientProvider>
  );
}
